// src/people/PeopleView.tsx
//
// BekalNanti — Milestone P3.6: People Management View
// Manages plan subjects/beneficiaries distinctly from the plan creator.

import React, { useState, useEffect } from 'react'
import { JourneyData, PersonRecord, GoalRecord } from '../journey/types'
import { formatRelationship, isPersonSelf } from '../lib/peopleService'
import { isEducationGoal } from '../lib/goalCapability'
import {
  Users,
  UserPlus,
  Edit2,
  Plus,
  Target,
  Calendar,
  GraduationCap,
  ArrowRight,
  RotateCcw,
} from 'lucide-react'

interface PeopleViewProps {
  journey: JourneyData
  onOpenAddPerson: () => void
  onEditPerson: (person: PersonRecord) => void
  onOpenAddPlanForPerson: (personId: string) => void
  onOpenPlan: (goal: GoalRecord) => void
  onEditMyProfile: () => void
  onRestorePerson?: (personId: string) => Promise<void>
}

export const PeopleView: React.FC<PeopleViewProps> = ({
  journey,
  onOpenAddPerson,
  onEditPerson,
  onOpenAddPlanForPerson,
  onOpenPlan,
  onEditMyProfile,
  onRestorePerson,
}) => {
  const { persons, goals, profile } = journey
  const [filterTab, setFilterTab] = useState<'active' | 'archived'>('active')

  const selfPerson = persons.find((p) => isPersonSelf(p))
  const otherPersons = persons.filter((p) => !isPersonSelf(p))

  const activeOtherPersons = otherPersons.filter((p) => p.status !== 'archived')
  const archivedOtherPersons =
    journey.archivedPersons && journey.archivedPersons.length > 0
      ? journey.archivedPersons
      : otherPersons.filter((p) => p.status === 'archived')

  const displayedOthers = filterTab === 'active' ? activeOtherPersons : archivedOtherPersons
  
  // Auto-reset filterTab if no archived persons remain
  useEffect(() => {
    if (filterTab === 'archived' && archivedOtherPersons.length === 0) {
      setFilterTab('active')
    }
  }, [archivedOtherPersons.length, filterTab])

  const getPersonGoals = (personId: string): GoalRecord[] => {
    return goals.filter((g) => g.personId === personId)
  }

  // Self goals can also include goals with personId === null (household baseline)
  const getSelfGoals = (): GoalRecord[] => {
    if (!selfPerson) return goals.filter((g) => !g.personId)
    return goals.filter((g) => g.personId === selfPerson.id || !g.personId)
  }

  const selfGoals = getSelfGoals()

  const calculateAge = (dobString?: string | null): number | null => {
    if (!dobString) return null
    const birthDate = new Date(dobString)
    if (isNaN(birthDate.getTime())) return null
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const m = today.getMonth() - birthDate.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age >= 0 ? age : null
  }

  return (
    <div className="space-y-6 text-left pb-16">
      {/* Top Banner / Heading */}
      <div className="rounded-3xl bg-gradient-to-br from-teal-50/80 via-white to-emerald-50/40 border border-teal-100/80 p-5 shadow-2xs space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white border border-teal-200 text-[#0F766E] text-[11px] font-bold shadow-2xs">
            <Users size={14} className="text-[#0EA5A4]" />
            <span>Keluarga &amp; Penerima Rencana</span>
          </span>

          <button
            type="button"
            onClick={onOpenAddPerson}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold transition-all cursor-pointer shadow-2xs active:scale-95"
          >
            <UserPlus size={14} />
            <span>Tambah Orang</span>
          </button>
        </div>

        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
            Orang yang Anda Kelola
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-relaxed">
            Rencana bekal dibuat untuk orang-orang tercinta. Anda dapat mendaftarkan beberapa anak, pasangan, orang tua, atau diri sendiri.
          </p>
        </div>
      </div>

      {/* 1. Self Card (Pengelola Utama) */}
      <div className="rounded-3xl border border-teal-200/80 bg-gradient-to-br from-white to-teal-50/30 p-5 shadow-sm space-y-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#0EA5A4] text-white flex items-center justify-center font-bold text-lg shadow-sm flex-shrink-0">
              {profile.displayName.slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-900">{profile.displayName}</h3>
                <span className="px-2 py-0.5 rounded-md bg-teal-100 text-[#0F766E] text-[10px] font-bold">
                  Saya (Pengelola)
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Pembuat dan penanggung jawab perencanaan keluarga
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onEditMyProfile}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold transition-all cursor-pointer shadow-2xs flex-shrink-0"
          >
            <Edit2 size={12} className="text-slate-500" />
            <span>Ubah Profil</span>
          </button>
        </div>

        {/* Self Plans Section */}
        <div className="pt-2 border-t border-slate-100 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-slate-700">Rencana Pribadi / Keluarga Bersama ({selfGoals.length})</span>
            {selfPerson && (
              <button
                type="button"
                onClick={() => onOpenAddPlanForPerson(selfPerson.id)}
                className="text-[#0EA5A4] hover:text-[#0F766E] font-semibold text-xs inline-flex items-center gap-1 cursor-pointer"
              >
                <Plus size={12} />
                <span>Tambah Rencana</span>
              </button>
            )}
          </div>

          {selfGoals.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {selfGoals.map((g) => (
                <div
                  key={g.id}
                  onClick={() => onOpenPlan(g)}
                  className="p-3 rounded-2xl bg-white border border-slate-200 hover:border-teal-300 transition-all cursor-pointer flex items-center justify-between group shadow-2xs"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-xl bg-slate-50 text-slate-600 group-hover:bg-teal-50 group-hover:text-[#0EA5A4] transition-colors flex items-center justify-center flex-shrink-0">
                      <Target size={15} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-800 truncate group-hover:text-[#0F766E] transition-colors">
                        {g.name}
                      </p>
                      <p className="text-[10px] text-slate-400 capitalize">
                        {g.goalType === 'retirement'
                          ? 'Pensiun'
                          : g.goalType === 'emergency'
                          ? 'Dana Darurat'
                          : g.goalType}
                      </p>
                    </div>
                  </div>
                  <ArrowRight size={13} className="text-slate-300 group-hover:text-[#0EA5A4] transition-colors flex-shrink-0" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">
              Belum ada rencana khusus untuk diri sendiri.
            </p>
          )}
        </div>
      </div>

      {/* 2. Beneficiaries Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              Anggota yang Didampingi ({activeOtherPersons.length})
            </h2>
            <p className="text-xs text-slate-500">
              Setiap anggota dapat memiliki rencana masa depan yang mandiri.
            </p>
          </div>

          {/* Active vs Archived Toggle */}
          {archivedOtherPersons.length > 0 && (
            <div className="flex items-center gap-1 p-0.5 rounded-xl bg-slate-100 border border-slate-200 text-xs">
              <button
                type="button"
                onClick={() => setFilterTab('active')}
                className={`px-3 py-1 rounded-lg font-semibold transition-all cursor-pointer ${
                  filterTab === 'active'
                    ? 'bg-white text-slate-900 shadow-2xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Aktif ({activeOtherPersons.length})
              </button>
              <button
                type="button"
                onClick={() => setFilterTab('archived')}
                className={`px-3 py-1 rounded-lg font-semibold transition-all cursor-pointer ${
                  filterTab === 'archived'
                    ? 'bg-white text-slate-900 shadow-2xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Diarsipkan ({archivedOtherPersons.length})
              </button>
            </div>
          )}
        </div>

        {/* People List */}
        {displayedOthers.length > 0 ? (
          <div className="space-y-3.5">
            {displayedOthers.map((person) => {
              const personGoals = getPersonGoals(person.id)
              const age = calculateAge(person.dateOfBirth)
              const isArchived = person.status === 'archived'

              return (
                <div
                  key={person.id}
                  className={`rounded-3xl border bg-white p-5 shadow-xs space-y-3.5 transition-all ${
                    isArchived
                      ? 'border-slate-200/60 bg-slate-50/50 opacity-75'
                      : 'border-slate-200/90 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-teal-50 text-[#0F766E] border border-teal-100 flex items-center justify-center font-bold text-base flex-shrink-0 shadow-2xs">
                        {person.fullName.slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-bold text-slate-900">{person.fullName}</h3>
                          <span className="px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-semibold">
                            {formatRelationship(person.relationship)}
                          </span>
                          {age !== null && (
                            <span className="text-[11px] text-slate-400">
                              • {age} tahun
                            </span>
                          )}
                          {isArchived && (
                            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                              Diarsipkan
                            </span>
                          )}
                        </div>
                        {person.dateOfBirth && (
                          <div className="flex items-center gap-1 text-[11px] text-slate-400 mt-0.5">
                            <Calendar size={11} />
                            <span>Lahir: {person.dateOfBirth}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      {isArchived && onRestorePerson && (
                        <button
                          type="button"
                          onClick={() => onRestorePerson(person.id)}
                          title="Pulihkan ke daftar aktif"
                          className="px-2.5 py-1 rounded-xl bg-teal-50 hover:bg-teal-100 text-[#0F766E] border border-teal-200 text-xs font-semibold inline-flex items-center gap-1 transition-colors cursor-pointer shadow-2xs"
                        >
                          <RotateCcw size={12} />
                          <span>Pulihkan</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onEditPerson(person)}
                        title="Ubah data orang"
                        className="p-1.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer"
                        aria-label="Ubah data"
                      >
                        <Edit2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Person Plans */}
                  <div className="pt-2 border-t border-slate-100 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-slate-700">
                        Rencana untuk {person.fullName} ({personGoals.length})
                      </span>
                      {!isArchived && (
                        <button
                          type="button"
                          onClick={() => onOpenAddPlanForPerson(person.id)}
                          aria-label={`Buat Rencana untuk ${person.fullName}`}
                          className="text-[#0EA5A4] hover:text-[#0F766E] font-semibold text-xs inline-flex items-center gap-1 cursor-pointer"
                        >
                          <Plus size={12} />
                          <span>Buat Rencana</span>
                        </button>
                      )}
                    </div>

                    {personGoals.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {personGoals.map((g) => {
                          const isEdu = isEducationGoal(g.goalType)
                          return (
                            <div
                              key={g.id}
                              onClick={() => onOpenPlan(g)}
                              className="p-3 rounded-2xl bg-slate-50/70 border border-slate-200/80 hover:border-teal-300 hover:bg-white transition-all cursor-pointer flex items-center justify-between group shadow-2xs"
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-8 h-8 rounded-xl bg-white text-slate-600 group-hover:bg-teal-50 group-hover:text-[#0EA5A4] border border-slate-200/60 transition-colors flex items-center justify-center flex-shrink-0">
                                  {isEdu ? <GraduationCap size={15} /> : <Target size={15} />}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-bold text-slate-800 truncate group-hover:text-[#0F766E] transition-colors">
                                    {g.name}
                                  </p>
                                  <p className="text-[10px] text-slate-400 capitalize">
                                    {isEdu ? 'Pendidikan' : g.goalType}
                                  </p>
                                </div>
                              </div>
                              <ArrowRight size={13} className="text-slate-300 group-hover:text-[#0EA5A4] transition-colors flex-shrink-0" />
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="py-3 px-4 rounded-2xl bg-slate-50/60 border border-dashed border-slate-200 flex items-center justify-between text-xs text-slate-400">
                        <span>Belum ada rencana yang dikaitkan ke {person.fullName}.</span>
                        {!isArchived && (
                          <button
                            type="button"
                            onClick={() => onOpenAddPlanForPerson(person.id)}
                            className="font-bold text-[#0EA5A4] hover:text-[#0F766E] cursor-pointer"
                          >
                            + Buat Rencana
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="py-10 px-6 rounded-3xl border border-dashed border-slate-200 bg-white text-center space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center mx-auto">
              <Users size={24} />
            </div>
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-slate-900">
                {filterTab === 'archived'
                  ? 'Tidak ada anggota keluarga yang diarsipkan'
                  : 'Belum Ada Anggota Keluarga Lain'}
              </h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
                {filterTab === 'archived'
                  ? 'Semua anggota keluarga saat ini berstatus aktif.'
                  : 'Daftarkan anak, pasangan, atau orang tua untuk mulai menyusun rencana bekal mereka.'}
              </p>
            </div>
            {filterTab === 'archived' && (
              <button
                type="button"
                onClick={() => setFilterTab('active')}
                className="inline-flex items-center gap-1.5 py-2 px-3.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors cursor-pointer"
              >
                <span>Lihat Anggota Aktif</span>
              </button>
            )}
            {filterTab === 'active' && (
              <button
                type="button"
                onClick={onOpenAddPerson}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold shadow-2xs transition-all cursor-pointer"
              >
                <UserPlus size={14} />
                <span>Tambah Anggota Keluarga</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
